import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type DocumentType =
    | "bank-statements"
    | "bank-csv"
    | "check-images"
    | "invoices"
    | "forms"
    | "other-docs"
    | "transactions"
    | "fraud-analysis";

export interface RecentDocumentItem {
    id: string;
    docType: DocumentType;
    label: string;
    url: string;
    icon: string;
    timestamp: number;
    // For filtering by folder/project
    l1?: string;
    l2?: string;
}

interface RecentNavigationState {
    items: RecentDocumentItem[];
    maxItems: number;
    currentUserId: string | null;
}

const STORAGE_KEY_PREFIX = "recentDocuments";
const MAX_ITEMS = 20; // Keep more items for better history

// Get storage key for specific user
const getStorageKey = (userId: string | null): string => {
    if (!userId) return `${STORAGE_KEY_PREFIX}_anonymous`;
    return `${STORAGE_KEY_PREFIX}_${userId}`;
};

// Load from localStorage for specific user
const loadFromStorage = (userId: string | null): RecentDocumentItem[] => {
    try {
        const stored = localStorage.getItem(getStorageKey(userId));
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error("Error loading recent navigation from localStorage:", error);
    }
    return [];
};

// Save to localStorage for specific user
const saveToStorage = (items: RecentDocumentItem[], userId: string | null) => {
    try {
        localStorage.setItem(getStorageKey(userId), JSON.stringify(items));
    } catch (error) {
        console.error("Error saving recent documents to localStorage:", error);
    }
};

const initialState: RecentNavigationState = {
    items: [],
    maxItems: MAX_ITEMS,
    currentUserId: null,
};

const slice = createSlice({
    name: "RecentNavigation",
    initialState,
    reducers: {
        setUserId(state, action: PayloadAction<string | null>) {
            const newUserId = action.payload;

            // If user changed, load their recent items
            if (newUserId !== state.currentUserId) {
                state.currentUserId = newUserId;
                state.items = loadFromStorage(newUserId);
            }
        },
        addRecentDocument(state, action: PayloadAction<RecentDocumentItem>) {
            const newItem = action.payload;

            // Remove any existing item with the same ID (same doc type + l1 + l2)
            state.items = state.items.filter((item) => item.id !== newItem.id);

            // Add new item at the beginning
            state.items.unshift(newItem);

            // Keep only the most recent items
            if (state.items.length > state.maxItems) {
                state.items = state.items.slice(0, state.maxItems);
            }

            // Save to localStorage for current user
            saveToStorage(state.items, state.currentUserId);
        },
        removeRecentDocument(state, action: PayloadAction<string>) {
            state.items = state.items.filter((item) => item.id !== action.payload);
            saveToStorage(state.items, state.currentUserId);
        },
        clearRecentDocuments(state) {
            state.items = [];
            saveToStorage([], state.currentUserId);
        },
    },
});

export const {
    setUserId,
    addRecentDocument,
    removeRecentDocument,
    clearRecentDocuments
} = slice.actions;

export default slice.reducer;

